import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { businessAPI, reportsAPI } from '../../services/apiServices';
import { useFetch } from '../../hooks/useFetch';
import { Alert, EmptyState, Spinner } from '../../components/common/UI';
import MoneyValue from '../../components/common/MoneyValue';
import { useFeedback } from '../../contexts/FeedbackContext';
import {
  ArrowUpIcon,
  BriefcaseIcon,
  BusinessCardIcon,
  ClockIcon,
  PlusIcon,
  TrendingIcon,
  UsersIcon,
} from '../../components/common/Icons';
import QrCodeModal from '../business/QrCodeModal';
import { getDerivedBusinessStatus } from '../../utils/businessStatus';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSubscriptionRegularizationMessage,
  isSubscriptionLock,
  needsSubscriptionRegularization,
} from '../../utils/plans';
import { getBusinessClientName } from '../../utils/businessOwner';

const currentMonth = new Date().getMonth();
const currentYear = new Date().getFullYear();
const PROFILE_REQUIRED_FIELDS = ['name', 'email', 'street', 'number', 'neighborhood', 'city', 'state', 'zipCode'];
const isProfileIncomplete = (user = {}) => PROFILE_REQUIRED_FIELDS.some((field) => !String(user?.[field] || '').trim());

const fetchDashboardData = async () => {
  const [businessesRes, contactsRes, financialRes, hoursRes, progressRes] = await Promise.all([
    businessAPI.getAll(),
    reportsAPI.clients(),
    reportsAPI.financial(),
    reportsAPI.workedHours(),
    reportsAPI.progress(),
  ]);

  return {
    data: {
      businesses: businessesRes.data?.data || businessesRes.data || [],
      contacts: contactsRes.data?.data || contactsRes.data || [],
      financial: financialRes.data?.data || financialRes.data || [],
      hours: hoursRes.data?.data || hoursRes.data || [],
      progress: progressRes.data?.data || progressRes.data || [],
    },
  };
};

const isThisMonth = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
};

const statusClass = {
  pre_initial: 'bg-amber-50 text-amber-700',
  started: 'bg-amber-50 text-amber-700',
  in_progress: 'bg-emerald-50 text-emerald-700',
  paused: 'bg-rose-50 text-rose-700',
  aborted: 'bg-rose-50 text-rose-700',
  canceled: 'bg-red-50 text-red-700',
  completed: 'bg-sky-50 text-sky-700',
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useFeedback();
  const { user } = useAuth();
  const { data, loading, error } = useFetch(fetchDashboardData, []);
  const [qrOpen, setQrOpen] = useState(false);
  const [showProfileNotice, setShowProfileNotice] = useState(false);
  const subscription = user?.subscription || {};
  const subscriptionNeedsRegularization = needsSubscriptionRegularization(subscription);
  const regularizationMessage = getSubscriptionRegularizationMessage(subscription, t);

  const dashboard = useMemo(() => {
    const businesses = data?.businesses || [];
    const contacts = data?.contacts || [];
    const financial = data?.financial || [];
    const hours = data?.hours || [];
    const progress = data?.progress || [];

    const progressByBusiness = new Map(
      progress.map((business) => [business.id, getDerivedBusinessStatus(business.progressList)])
    );
    const financialByBusiness = new Map(
      financial.map((item) => [item.businessId, item])
    );

    const monthlyBalance = financial.reduce((sum, business) => {
      if (!business.entries?.length) return sum + Number(business.balance || 0);
      return sum + business.entries.reduce((entrySum, entry) => {
        if (!isThisMonth(entry.launchDate)) return entrySum;
        const amount = Number(entry.amount || 0);
        return entry.type === 'debit' ? entrySum - amount : entrySum + amount;
      }, 0);
    }, 0);

    const monthlyHours = hours.reduce((sum, business) => {
      if (!business.entries?.length) return sum + Number(business.totalHours || 0);
      return sum + business.entries.reduce((entrySum, entry) => (
        isThisMonth(entry.date) ? entrySum + Number(entry.totalHours || 0) : entrySum
      ), 0);
    }, 0);

    const activeContacts = contacts.filter((contact) => contact.isActive !== false).length;
    const businessesThisMonth = businesses.filter((business) => (
      isThisMonth(business.createdAt || business.startDate)
    )).length;

    const recentBusinesses = [...businesses]
      .sort((a, b) => new Date(b.createdAt || b.startDate) - new Date(a.createdAt || a.startDate))
      .slice(0, 3)
      .map((business) => ({
        ...business,
        latestStatus: progressByBusiness.get(business.id),
        financial: financialByBusiness.get(business.id),
      }));

    return {
      activeContacts,
      businesses,
      businessesThisMonth,
      monthlyHours,
      monthlyBalance,
      recentBusinesses,
    };
  }, [data]);

  useEffect(() => {
    if (!isProfileIncomplete(user)) {
      setShowProfileNotice(false);
      return undefined;
    }
    const showTimer = setTimeout(() => setShowProfileNotice(true), 3000);
    const hideTimer = setTimeout(() => setShowProfileNotice(false), 8000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [user]);

  const handleRecentBusinessClick = (event, business) => {
    if (!isSubscriptionLock(business.access, subscription) && !subscriptionNeedsRegularization) return;
    event.preventDefault();
    notify({
      variant: 'warning',
      title: t('plans.regularizeTitle'),
      message: getSubscriptionRegularizationMessage(business.access?.subscription || subscription, t, business.access?.plan || subscription.plan),
      confirmText: t('plans.regularizeCta'),
    }).then(() => navigate('/subscription'));
  };

  if (loading) return <Spinner size="lg" />;

  return (
    <div className="mx-auto max-w-[1104px]">
      <div className="mb-9 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="mt-1 text-base text-slate-600">
            {t('dashboardWeb.subtitle')}
          </p>
        </div>
        <Link
          to="/businesses/new"
          className="inline-flex h-10 items-center justify-center gap-3 rounded-lg bg-sky-500 px-6 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600"
        >
          <PlusIcon className="h-4 w-4" />
          {t('businesses.new')}
        </Link>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {showProfileNotice && (
        <Alert variant="info" onClose={() => setShowProfileNotice(false)} style={{ marginBottom: '1rem' }}>
          <Link to="/profile" className="font-bold text-sky-700">{t('profile.completeProfileNotice')}</Link>
        </Alert>
      )}
      {subscriptionNeedsRegularization && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-black">{t('plans.regularizeTitle')}</p>
              <p className="mt-1">{regularizationMessage}</p>
            </div>
            <Link to="/subscription" className="inline-flex h-9 items-center justify-center rounded-lg bg-amber-600 px-4 font-bold text-white">
              {t('plans.regularizeCta')}
            </Link>
          </div>
        </div>
      )}
      {subscription?.isTrialActive && !subscriptionNeedsRegularization && (
        <div className="mb-6 rounded-lg border border-sky-100 bg-sky-50 p-4 text-sm text-sky-900">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>{t('profile.subscribeDuringTrialNotice')}</span>
            <Link to="/subscription" className="inline-flex h-9 items-center justify-center rounded-lg bg-sky-500 px-4 font-bold text-white">
              {t('profile.subscribeNow')}
            </Link>
          </div>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={<BriefcaseIcon className="h-6 w-6" />}
          iconClass="bg-sky-50 text-sky-500"
          label={t('dashboardWeb.totalBusinesses')}
          value={dashboard.businesses.length}
          footer={t('dashboardWeb.thisMonthCount', { count: dashboard.businessesThisMonth })}
        />
        <SummaryCard
          icon={<UsersIcon className="h-6 w-6" />}
          iconClass="bg-emerald-50 text-emerald-600"
          label={t('dashboardWeb.activeContacts')}
          value={dashboard.activeContacts}
          footer={t('dashboardWeb.registeredContacts')}
        />
        <SummaryCard
          icon={<TrendingIcon className="h-6 w-6" />}
          iconClass="bg-amber-50 text-amber-700"
          label={t('dashboardWeb.monthlyRevenue')}
          value={<MoneyValue value={dashboard.monthlyBalance} minimumFractionDigits={0} maximumFractionDigits={0} />}
          footer={t('dashboardWeb.currentMonth')}
        />
        <SummaryCard
          icon={<ClockIcon className="h-6 w-6" />}
          iconClass="bg-orange-50 text-orange-700"
          label={t('dashboardWeb.workedHours')}
          value={`${Number(dashboard.monthlyHours || 0).toFixed(0)}h`}
          footer={t('dashboardWeb.thisMonth')}
        />
      </div>

      <section className="mt-8 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
        <div className="flex h-[76px] items-center justify-between border-b border-slate-200 px-6">
          <h2 className="text-lg font-bold text-slate-900">{t('dashboardWeb.recentBusinesses')}</h2>
          <Link to="/businesses" className="text-sm font-semibold text-sky-500 hover:text-sky-600">
            {t('actions.viewAll')}
          </Link>
        </div>

        {dashboard.recentBusinesses.length === 0 ? (
          <EmptyState
            icon=""
            title={t('dashboardWeb.emptyTitle')}
            description={t('dashboardWeb.emptyDescription')}
            action={
              <Link
                to="/businesses/new"
                className="inline-flex h-10 items-center rounded-lg bg-sky-500 px-4 text-sm font-bold text-white"
              >
                {t('businesses.create')}
              </Link>
            }
          />
        ) : (
          <div className="divide-y divide-slate-200">
            {dashboard.recentBusinesses.map((business) => {
              const status = business.latestStatus;
              return (
                <Link
                  to={`/businesses/${business.id}`}
                  key={business.id}
                  onClick={(event) => handleRecentBusinessClick(event, business)}
                  className="flex min-h-[92px] items-center gap-4 px-6 py-4 transition hover:bg-slate-50"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-500">
                    <BriefcaseIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-slate-900">{business.name}</p>
                    <p className="truncate text-sm text-slate-600">
                      {getBusinessClientName(business, user, t)}
                    </p>
                  </div>
                  {status && (
                    <span className={`hidden rounded-full px-3 py-1 text-xs font-semibold sm:inline-flex ${statusClass[status] || 'bg-slate-100 text-slate-600'}`}>
                      {t(`status.${status}`, status)}
                    </span>
                  )}
                  <strong className="max-w-[150px] break-words text-right text-base leading-tight text-slate-900">
                    <MoneyValue value={business.financial?.balance || 0} minimumFractionDigits={0} maximumFractionDigits={0} />
                  </strong>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <button
        className="fixed bottom-5 right-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-sky-500 shadow-soft ring-1 ring-slate-200 transition hover:bg-sky-50 sm:hidden"
        onClick={() => setQrOpen(true)}
        aria-label="Gerar QR Code"
      >
        <BusinessCardIcon className="h-5 w-5" />
      </button>
      <QrCodeModal isOpen={qrOpen} onClose={() => setQrOpen(false)} />
    </div>
  );
};

const SummaryCard = ({ icon, iconClass, label, value, footer }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-soft">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-slate-600">{label}</p>
        <p className="mt-1 max-w-[190px] break-words text-2xl font-bold leading-tight text-slate-900">{value}</p>
      </div>
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconClass}`}>
        {icon}
      </div>
    </div>
    <div className="mt-3 flex items-center gap-1 text-xs font-medium text-slate-700">
      <ArrowUpIcon className="h-4 w-4 text-emerald-600" />
      <span>{footer}</span>
    </div>
  </div>
);

export default DashboardPage;
